import styled from 'styled-components';

import { Colors } from '@blueprintjs/core';

export const Toolbar = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
`;

export const Controls = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 12px;

    label {
        display: block;
        margin-bottom: 4px;
        font-size: 12px;
        color: ${Colors.GRAY4};
    }
`;

export const BarList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

export const BarRowButton = styled.div<{ $selected: boolean; $emphasized?: boolean }>`
    display: grid;
    grid-template-columns: minmax(120px, 220px) minmax(160px, 1fr);
    align-items: center;
    column-gap: 16px;
    padding: 4px 8px;
    border-radius: 4px;
    cursor: pointer;
    background-color: ${({ $selected }) => ($selected ? 'rgba(255, 255, 255, 0.06)' : 'transparent')};

    &:hover {
        background-color: rgba(255, 255, 255, 0.08);
    }

    &:focus-visible {
        outline: 2px solid ${Colors.BLUE4};
        outline-offset: 1px;
    }

    & > span:first-child {
        overflow: hidden;
        text-align: right;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-weight: ${({ $emphasized, $selected }) => ($emphasized || $selected ? 700 : 400)};
        text-transform: ${({ $emphasized }) => ($emphasized ? 'uppercase' : 'none')};
    }
`;

export const BarDivider = styled.hr`
    width: 100%;
    margin: 4px 0;
    border: none;
    border-top: 1px solid ${Colors.DARK_GRAY5};
`;

export const BarTrack = styled.span`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
`;

export const BarFill = styled.span<{ $width: number; $color: string; $selected?: boolean }>`
    display: inline-block;
    flex: 0 0 auto;
    width: ${({ $width }) => `${$width}%`};
    min-width: 2px;
    height: 14px;
    border-radius: 2px;
    background-color: ${({ $color }) => $color};
    box-shadow: ${({ $selected }) => ($selected ? `0 0 0 2px ${Colors.WHITE}` : 'none')};
`;

export const ScrollArea = styled.div`
    width: 100%;
    overflow-x: auto;
`;

// Top margin is headroom for bar labels: the tallest bar reaches the top and the scroll area clips
export const Chart = styled.div<{ $minWidth: number }>`
    position: relative;
    display: flex;
    align-items: stretch;
    min-width: ${({ $minWidth }) => `${$minWidth}px`};
    height: 260px;
    margin-top: 32px;
    padding-left: 44px;
    border-bottom: 1px solid ${Colors.GRAY1};
`;

export const ChartGridLine = styled.div<{ $bottom: number }>`
    position: absolute;
    left: 44px;
    right: 0;
    bottom: ${({ $bottom }) => `${$bottom}%`};
    border-top: 1px solid ${Colors.DARK_GRAY5};

    & > span {
        position: absolute;
        left: -44px;
        width: 38px;
        transform: translateY(-50%);
        text-align: right;
        font-size: 11px;
        color: ${Colors.GRAY4};
    }
`;

export const ChartGroup = styled.div`
    position: relative;
    display: flex;
    flex: 1 1 0;
    align-items: flex-end;
    justify-content: center;
    gap: 2px;
    padding: 0 6px;
`;

export const ChartBar = styled.div<{ $height: number; $color: string }>`
    position: relative;
    width: 12px;
    height: ${({ $height }) => `${$height}%`};
    min-height: 1px;
    background-color: ${({ $color }) => $color};
    border-radius: 2px 2px 0 0;
`;

// Vertical, reading upward, so labels fit the 12px bar pitch without colliding with neighbours
export const ChartBarLabel = styled.span`
    position: absolute;
    bottom: calc(100% + 3px);
    left: 50%;
    transform: translateX(-50%) rotate(180deg);
    writing-mode: vertical-rl;
    font-size: 10px;
    font-weight: 600;
    line-height: 12px;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    color: ${Colors.LIGHT_GRAY5};
`;

export const ChartLabels = styled.div<{ $minWidth: number }>`
    display: flex;
    min-width: ${({ $minWidth }) => `${$minWidth}px`};
    padding-left: 44px;

    & > span {
        flex: 1 1 0;
        padding-top: 6px;
        text-align: center;
        font-size: 12px;
        white-space: nowrap;
    }
`;

export const Legend = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px 16px;
    margin-top: 12px;
    font-size: 12px;

    & > span {
        display: inline-flex;
        align-items: center;
        gap: 6px;
    }
`;

export const Swatch = styled.span<{ $color: string }>`
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 2px;
    background-color: ${({ $color }) => $color};
`;

export const TableScroll = styled.div`
    width: 100%;
    overflow-x: auto;

    table {
        width: 100%;
        white-space: nowrap;
    }

    td,
    th {
        vertical-align: middle !important;
    }
`;
